import {
  Box,
  IconButton,
  Typography,
  useTheme,
  Avatar,
  Popper,
  Paper,
  Tooltip,
  InputBase,
  Divider,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Drawer,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import AddCircleOutlineSharpIcon from "@mui/icons-material/AddCircleOutlineSharp";
import FilterListIcon from "@mui/icons-material/FilterList";
import {
  DarkModeOutlined,
  LightModeOutlined,
  LogoutOutlined,
  NewReleases,
  SettingsOutlined,
  SupportOutlined,
} from "@mui/icons-material";
import { useContext, useRef, useState } from "react";
import { ColorModeContext, tokens } from "../theme";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import type { RootState, AppDispatch } from "../store/Index";
import { logout, type User } from "../store/slice/AuthSlice";
import Breadcrumbs from "../components/common/BreadCrumbs";
import NotificationBellWithPopover from "./Notifications";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import ToolsAssetsDrawer from "./ToolsAssetsDrawer";

const TopBar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const colorMode = useContext(ColorModeContext);
  const avatarRef = useRef<HTMLDivElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [openPopper, setOpenPopper] = useState(false);
  const [openToolsDrawer, setOpenToolsDrawer] = useState(false);
  const [openMobileMenu, setOpenMobileMenu] = useState(false);

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const user = useSelector(
    (state: RootState) => state.auth.user
  ) as User | null;
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const closePopperTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // --- Popper handlers ---
  const clearTimeoutRef = () => {
    if (closePopperTimeoutRef.current !== null) {
      clearTimeout(closePopperTimeoutRef.current);
      closePopperTimeoutRef.current = null;
    }
  };

  const handleOpenPopper = () => {
    clearTimeoutRef();
    setAnchorEl(avatarRef.current);
    setOpenPopper(true);
  };

  const handleClosePopper = () => {
    clearTimeoutRef();
    closePopperTimeoutRef.current = setTimeout(() => {
      setOpenPopper(false);
    }, 200);
  };

  // --- Drawer handlers ---
  const handleOpenTools = () => setOpenToolsDrawer(true);
  const handleCloseTools = () => setOpenToolsDrawer(false);

  const handleOpenMobileMenu = () => setOpenMobileMenu(true);
  const handleCloseMobileMenu = () => setOpenMobileMenu(false);

  // --- Profile menu actions ---
  const handleWhatsNew = () => {
    setOpenPopper(false);
    navigate("/changelog");
  };
  const handleLogout = () => {
    dispatch(logout());
    setOpenPopper(false);
    navigate("/login");
  };
  const handleSettings = () => {
    navigate("/settings");
    setOpenPopper(false);
  };
  const handleSupport = () => {
    window.open("https://muzukuru.com/tickets", "_blank");
  };

  if (!user) return null;

  const avatarInitial = user.username?.charAt(0)?.toUpperCase() || "?";

  return (
    <Box
      className="topbar"
      width="100%"
      sx={{
        borderRadius: "8px",
        position: "sticky",
        backgroundColor: colors.primary[800],
        pl: { xs: 0, md: 2 },
        top: 0,
        zIndex: 10,
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={2}
        sx={{
          backgroundColor: colors.primary[800],
          gap: { xs: 1, md: 2 },
        }}
      >
        {/* Left: Mobile Menu or Breadcrumbs */}
        <Box
          display="flex"
          alignItems="center"
          sx={{ minWidth: 0, flex: { xs: "0 0 auto", md: "1 1 auto" } }}
        >
          {isMobile ? (
            <IconButton
              onClick={handleOpenMobileMenu}
              sx={{ p: 0.5, backgroundColor: "transparent" }}
              aria-label="menu"
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <Breadcrumbs />
          )}
        </Box>

        {/* Middle: Search Bar - Hidden on mobile, visible on desktop */}
        {!isMobile && (
          <Box
            sx={{
              backgroundColor: colors.primary[900],
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              pl: 1,
              width: "25%",
            }}
          >
            <SearchIcon sx={{ mr: 1 }} />
            <InputBase
              placeholder="Search..."
              sx={{ ml: 1, flex: 1 }}
              disabled
            />
            <IconButton aria-label="Filter results">
              <FilterListIcon />
            </IconButton>
          </Box>
        )}

        {/* Right: Actions */}
        <Box
          display="flex"
          alignItems="center"
          sx={{
            gap: { xs: 0.5, md: 1.5 },
            justifyContent: "flex-end",
          }}
        >
          {isMobile && (
            <Tooltip title="Search" arrow>
              <IconButton
                aria-label="Search"
                sx={{ backgroundColor: colors.neutral[700] }}
              >
                <SearchIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {!isMobile && (
            <>
              <Tooltip title="Add Booking" arrow>
                <IconButton
                  aria-label="Add booking"
                  sx={{ backgroundColor: colors.neutral[700] }}
                >
                  <AddCircleOutlineSharpIcon
                    onClick={() => navigate("bookings/add/funeral")}
                  />
                </IconButton>
              </Tooltip>

              <Tooltip title="Tool assets" arrow>
                <IconButton
                  aria-label="Tool assets"
                  sx={{ backgroundColor: colors.neutral[700] }}
                  onClick={handleOpenTools}
                >
                  <GridViewOutlinedIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Toggle theme" arrow>
                <IconButton
                  sx={{
                    backgroundColor: colors.neutral[700],
                  }}
                  aria-label="Toggle theme"
                  onClick={colorMode.toggleColorMode}
                  disabled
                >
                  {theme.palette.mode === "dark" ? (
                    <LightModeOutlined />
                  ) : (
                    <DarkModeOutlined />
                  )}
                </IconButton>
              </Tooltip>

              <NotificationBellWithPopover />
            </>
          )}

          {/* --- Profile Avatar and Menu --- */}
          <Box
            ref={avatarRef}
            onMouseEnter={!isMobile ? handleOpenPopper : undefined}
            onMouseLeave={!isMobile ? handleClosePopper : undefined}
            onClick={isMobile ? handleOpenPopper : undefined}
            onKeyDown={(e) => e.key === "Enter" && handleOpenPopper()}
            tabIndex={0}
            sx={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              border: `1px solid ${colors.primary[300]}`,
              borderRadius: "8px",
              p: 0.5,
            }}
            role="button"
            aria-label="User profile"
          >
            <Avatar sx={{ width: 32, height: 32 }}>{avatarInitial}</Avatar>
            {!isMobile && (
              <Box sx={{ ml: 1, textAlign: "left" }}>
                <Typography variant="subtitle2">
                  {user?.first_name && user?.last_name
                    ? `${user.first_name} ${user.last_name}`
                    : user?.username || "Guest"}
                </Typography>
              </Box>
            )}
          </Box>

          <Popper
            open={openPopper}
            anchorEl={anchorEl}
            placement="bottom-end"
            sx={{ zIndex: 11 }}
            modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
          >
            <Paper
              onMouseEnter={!isMobile ? handleOpenPopper : undefined}
              onMouseLeave={!isMobile ? handleClosePopper : undefined}
              elevation={3}
              sx={{ width: 250, borderRadius: theme.shape.borderRadius }}
            >
              <Box
                sx={{
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Avatar sx={{ width: 32, height: 32 }}>{avatarInitial}</Avatar>
                <Typography variant="subtitle1" fontWeight="bold">
                  {user.first_name} {user.last_name}
                </Typography>
              </Box>
              <Divider />
              <ListItemButton onClick={handleWhatsNew} aria-label="What's New">
                <ListItemIcon>
                  <NewReleases />
                </ListItemIcon>
                <ListItemText primary="What's New" />
              </ListItemButton>
              <ListItemButton onClick={handleSettings} aria-label="Settings">
                <ListItemIcon>
                  <SettingsOutlined />
                </ListItemIcon>
                <ListItemText primary="Settings" />
              </ListItemButton>
              <ListItemButton onClick={handleSupport} aria-label="Support">
                <ListItemIcon>
                  <SupportOutlined />
                </ListItemIcon>
                <ListItemText primary="Support" />
              </ListItemButton>
              <ListItemButton onClick={handleLogout} aria-label="Logout">
                <ListItemIcon>
                  <LogoutOutlined />
                </ListItemIcon>
                <ListItemText primary="Logout" />
              </ListItemButton>
            </Paper>
          </Popper>
        </Box>
      </Box>

      <Drawer
        anchor="left"
        open={openMobileMenu}
        onClose={handleCloseMobileMenu}
      >
        <Box
          sx={{
            width: 280,
            p: 2,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            backgroundColor: colors.primary[800],
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6" fontWeight="bold">
              Menu
            </Typography>
            <IconButton
              onClick={handleCloseMobileMenu}
              aria-label="close menu"
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}>
            <ListItemButton
              onClick={() => {
                navigate("bookings/add/funeral");
                handleCloseMobileMenu();
              }}
            >
              <ListItemIcon>
                <AddCircleOutlineSharpIcon />
              </ListItemIcon>
              <ListItemText primary="Add Booking" />
            </ListItemButton>
            <ListItemButton
              onClick={() => {
                handleOpenTools();
                handleCloseMobileMenu();
              }}
            >
              <ListItemIcon>
                <GridViewOutlinedIcon />
              </ListItemIcon>
              <ListItemText primary="Tool Assets" />
            </ListItemButton>
            <ListItemButton onClick={colorMode.toggleColorMode} disabled>
              <ListItemIcon>
                {theme.palette.mode === "dark" ? (
                  <LightModeOutlined />
                ) : (
                  <DarkModeOutlined />
                )}
              </ListItemIcon>
              <ListItemText primary="Toggle Theme" />
            </ListItemButton>
            <ListItemButton
              onClick={() => {
                navigate("/changelog");
                handleCloseMobileMenu();
              }}
            >
              <ListItemIcon>
                <NewReleases />
              </ListItemIcon>
              <ListItemText primary="What's New" />
            </ListItemButton>
            <ListItemButton
              onClick={() => {
                navigate("/settings");
                handleCloseMobileMenu();
              }}
            >
              <ListItemIcon>
                <SettingsOutlined />
              </ListItemIcon>
              <ListItemText primary="Settings" />
            </ListItemButton>
            <ListItemButton onClick={handleSupport}>
              <ListItemIcon>
                <SupportOutlined />
              </ListItemIcon>
              <ListItemText primary="Support" />
            </ListItemButton>
            <Divider sx={{ my: 1 }} />
            <ListItemButton onClick={handleLogout}>
              <ListItemIcon>
                <LogoutOutlined />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItemButton>
          </Box>
        </Box>
      </Drawer>

      {/* --- Right-side Drawer --- */}
      <ToolsAssetsDrawer open={openToolsDrawer} onClose={handleCloseTools} />
    </Box>
  );
};

export default TopBar;
